<script setup lang="tsx">
import type { Permission } from '@/models/enums-generated'
import { computed } from 'vue'

const props = defineProps<{
	all: Permission
	own?: Permission
	title?: string
	title2?: string
	readOnly: boolean
}>()
const model = defineModel<Permission[]>({ required: true })
const isOwn = computed(() => props.own && model.value.includes(props.own))
const isAll = computed(() => model.value.includes(props.all))

function add(perm: Permission) {
	remove()
	model.value.push(perm)
}

function remove() {
	if (props.all) {
		const idx = model.value.indexOf(props.all)
		if (idx >= 0) model.value.splice(idx, 1)
	}
	if (props.own) {
		const idx = model.value.indexOf(props.own)
		if (idx >= 0) model.value.splice(idx, 1)
	}
}
</script>

<template>
	<div class="btn-group" :class="{ 'has-title2': props.title2 }">
		<div class="btn-wrapper" v-if="props.own">
			<button class="btn btn-slider own-btn" :class="{ active: isOwn }" @click="add(props.own)" :disabled="props.readOnly">
				{{ props.title2 ? props.title : $t('misc.own') }}
			</button>
			<span v-if="isOwn" class="dot"></span>
		</div>
		<div class="btn-wrapper admin-text" v-else-if="props.title">
			<span>{{ props.title }}</span>
		</div>
		<div class="btn-wrapper">
			<button class="btn btn-slider remove-btn" :class="{ active: !isOwn && !isAll }" @click="remove" :disabled="props.readOnly">X</button>
			<span v-if="!isOwn && !isAll" class="dot"></span>
		</div>
		<div class="btn-wrapper">
			<button v-if="props.all" class="btn btn-slider all-btn" :class="{ active: isAll }" @click="add(props.all)" :disabled="props.readOnly">
				{{ props.own ? props.title2 || $t('misc.all') : 'ON' }}
			</button>
			<span v-if="isAll" class="dot"></span>
		</div>
	</div>
</template>

<style lang="sass" scoped>
.btn-group
  display: inline-flex
  border-radius: 20px
  box-shadow: 0 0 0 1px #ccc
  background-color: var(--bs-body-bg)
  border-radius: 9px
  border: none

.btn-wrapper
  .own-btn.active + .dot
    background-color: #4944c3

  .all-btn.active + .dot
    margin-left: -2px
    background-color: #81d448
  .remove-btn.active + .dot
    margin-left: -2px

.dot
  width: 4px
  height: 4px
  border-radius: 50%
  background-color: var(--bs-body-color)
  position: absolute
  top: -7px

.btn-slider
  display: flex
  align-items: center
  justify-content: center
  width: 44px
  height: 20px
  border-radius: 9px
  border: none
  cursor: pointer
  background-color: var(--bs-body-bg)
  color: #333
  font-size: 12px
  font-weight: bold
  padding: 0

.admin-text
  width: 120px
  color: #333
  font-size: 12px
  font-weight: bold

.own-btn
  width: 64px
  margin-right: -15px
  z-index: 1
  color: #928f8d
  background-color: var(--bs-body-bg)
  padding-right: 12px

.own-btn.active
  color: white
  background-color: #4944c3

.all-btn
  width: 64px
  margin-left: -15px
  z-index: 1
  color: #928f8d
  background-color: var(--bs-body-bg)
  padding-left: 12px

.all-btn.active
  color: white
  background-color: #81d448

.btn-slider:active
  background-color: #ddd

.remove-btn
  width: 30px
  height: 20px
  border-radius: 50%
  z-index: 3
  position: relative
  color: #928f8d
  background-color: var(--bs-body-bg)

.remove-btn.active
  color: var(--bs-body-color)
  background-color: var(--bs-secondary-bg)

.btn-group.has-title2
  .own-btn,
  .all-btn
    width: 100px
    justify-content: flex-start
    padding-left: 20px
    padding-right: 12px
</style>
